import {
  Component,
  inject,
  ViewChild,
  ViewChildren,
  QueryList,
  HostListener,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { Drawer } from 'primeng/drawer';
import { SelectModule } from 'primeng/select';
import { MultiSelect, MultiSelectModule } from 'primeng/multiselect';
import { IftaLabelModule } from 'primeng/iftalabel';
import { DrawerModule } from 'primeng/drawer';
import { ChipModule } from 'primeng/chip';
import { Owner, Region } from './advanced-filter.data';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  Subject,
  BehaviorSubject,
  Observable,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  switchMap,
  takeUntil,
  of,
  take,
} from 'rxjs';
import { EntityRef, GlobalFilter, GlobalHotel } from '@core/models';
import { GlobalPropertyRepository } from '@store/global-properties.repository';
import { GlobalFilterRepository } from '@store/global-filter.repository';
import { SkeletonModule } from 'primeng/skeleton';
import { DateRange } from '../date-picker/date-picker.component';
import { reorderItems } from '@core/utils/reorder-items';

interface FilterFormControls {
  selectedEnterprises: FormControl<EntityRef[] | null>;
  selectedBrands: FormControl<EntityRef[] | null>;
  selectedHotels: FormControl<GlobalHotel[] | null>;
  selectedRegions: FormControl<Region[] | null>;
  selectedOwners: FormControl<Owner[] | null>;
}

@Component({
  selector: 'cwa-advanced-filter',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    MultiSelectModule,
    DrawerModule,
    SelectModule,
    ChipModule,
    IftaLabelModule,
    SkeletonModule,
    ReactiveFormsModule,
  ],
  templateUrl: './advanced-filter.component.html',
})
export class AdvancedFilterComponent implements OnInit, OnDestroy {
  @ViewChild('advancedFilterDrawer') advancedFilterDrawer!: Drawer;
  @ViewChildren(MultiSelect) multiSelects!: QueryList<MultiSelect>;

  @Output() dateRangeChanged = new EventEmitter<DateRange>();

  // Drawer state
  drawerVisible = false;

  private readonly formBuilder = inject(FormBuilder);
  private readonly globalPropRepo = inject(GlobalPropertyRepository);
  private readonly globalFilterRepo = inject(GlobalFilterRepository);
  private readonly destroy$ = new Subject<void>();

  isEnterpriseLoading = false;
  isBrandLoading = false;
  isHotelLoading = false;

  // Form
  filterForm!: FormGroup<FilterFormControls>;

  // Observable data streams for dropdowns (from API)
  enterprises$!: Observable<EntityRef[]>;
  brands$!: Observable<EntityRef[]>;
  hotels$!: Observable<GlobalHotel[]>;
  regions$!: Observable<Region[]>;
  owners$!: Observable<Owner[]>;

  // Search filters for typeahead
  private enterpriseSearch$ = new Subject<string>();
  private brandSearch$ = new Subject<string>();
  private hotelSearch$ = new Subject<string>();
  private regionSearch$ = new Subject<string>();
  private ownerSearch$ = new Subject<string>();

  // Triggers to re-order selected Brand/Hotel options to the top when the panel closes
  private enterpriseReorderTrigger$ = new BehaviorSubject<void>(undefined);
  private brandReorderTrigger$ = new BehaviorSubject<void>(undefined);
  private hotelReorderTrigger$ = new BehaviorSubject<void>(undefined);
  private ownerReorderTrigger$ = new BehaviorSubject<void>(undefined);
  private regionReorderTrigger$ = new BehaviorSubject<void>(undefined);

  // Single hotel selection mode flag
  selectSingleHotel: boolean = false;

  ngOnInit(): void {
    this.buildForm();
    this.initializeDataStreams();
    this.setupSearchFilters();
    this.syncFormFromStore();
    this.subscribeToSingleHotelMode();
  }

  // Component-scoped fetch wrappers: loading state is tied to THIS component's
  // own requests, not the shared singleton service loading state. This prevents
  private fetchEnterprises$(
    ownerIds: string[] = [],
    regionIds: string[] = [],
    search?: string
  ): Observable<EntityRef[]> {
    this.isEnterpriseLoading = true;
    return this.globalPropRepo
      .fetchEnterprises(ownerIds, regionIds, search)
      .pipe(finalize(() => (this.isEnterpriseLoading = false)));
  }

  private fetchBrands$(
    enterpriseIds: string[],
    ownerIds: string[] = [],
    regionIds: string[] = [],
    search?: string
  ): Observable<EntityRef[]> {
    this.isBrandLoading = true;
    return this.globalPropRepo
      .fetchBrands(enterpriseIds, ownerIds, regionIds, search)
      .pipe(finalize(() => (this.isBrandLoading = false)));
  }

  private fetchHotels$(
    brandIds: string[],
    enterpriseIds: string[],
    ownerIds: string[] = [],
    regionIds: string[] = [],
    search?: string
  ): Observable<GlobalHotel[]> {
    this.isHotelLoading = true;
    return this.globalPropRepo
      .fetchHotels(brandIds, enterpriseIds, ownerIds, regionIds, search)
      .pipe(finalize(() => (this.isHotelLoading = false)));
  }

  // Dynamic placeholders based on loading state - ADD THESE GETTERS
  get enterprisePlaceholder(): string {
    return this.isEnterpriseLoading ? 'Enterprise Loading...' : 'Select Enterprise';
  }

  get brandPlaceholder(): string {
    return this.isBrandLoading ? 'Brand Loading...' : 'Select Brand';
  }

  get hotelPlaceholder(): string {
    return this.isHotelLoading ? 'Hotel Loading...' : 'Select Hotel';
  }

  private subscribeToSingleHotelMode(): void {
    // Subscribe to single hotel selection mode changes from pages like Property Report
    this.globalFilterRepo.selectSingleHotel$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isSingleMode) => {
        this.selectSingleHotel = isSingleMode;

        // When entering single-select mode, enforce single hotel selection
        if (isSingleMode) {
          this.enforceSingleHotelSelection();
        }
      });
  }

  private enforceSingleHotelSelection(): void {
    // When switching to single-select mode (e.g., navigating from Channel Mix to Property Report)
    // ensure only ONE hotel is selected, even if multiple were selected before
    const currentSelection = this.filterForm.value.selectedHotels || [];

    if (currentSelection.length > 1) {
      // Multiple hotels selected - keep only the FIRST one
      const firstHotel = currentSelection[0];

      this.filterForm.patchValue(
        {
          selectedHotels: [firstHotel],
        },
        { emitEvent: false }
      );

      // Store hotel info for display in UI
      this.globalFilterRepo.setSelectedHotelInfo({
        id: firstHotel.id,
        name: firstHotel.name,
        code: firstHotel.code || '',
        displayName: firstHotel.displayName,
      });

      // Update global filter state with single hotel
      this.saveToGlobalFilter();
    } else if (currentSelection.length === 1) {
      // Already one hotel selected - just ensure hotel info is stored
      const hotel = currentSelection[0];
      this.globalFilterRepo.setSelectedHotelInfo({
        id: hotel.id,
        name: hotel.name,
        code: hotel.code || '',
        displayName: hotel.displayName,
      });
    }
    // If 0 hotels selected, do nothing (empty state is valid)
  }

  // Helper functions for chip count display
  hasRemainingItems(type: 'enterprises' | 'owners' | 'regions'): boolean {
    const items = this.getItemsArray(type);
    return items ? items.length > 2 : false;
  }

  getRemainingCount(type: 'enterprises' | 'owners' | 'regions'): string {
    const items = this.getItemsArray(type);
    const count = items ? items.length - 2 : 0;
    return `+${count}`;
  }

  private getItemsArray(type: 'enterprises' | 'owners' | 'regions'): any[] | null | undefined {
    switch (type) {
      case 'enterprises':
        return this.filterForm.value.selectedEnterprises;
      case 'owners':
        return this.filterForm.value.selectedOwners;
      case 'regions':
        return this.filterForm.value.selectedRegions;
      default:
        return null;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildForm(): void {
    this.filterForm = this.formBuilder.group<FilterFormControls>({
      selectedEnterprises: this.formBuilder.control<EntityRef[]>([]),
      selectedBrands: this.formBuilder.control<EntityRef[]>([]),
      selectedHotels: this.formBuilder.control<GlobalHotel[]>([]),
      selectedRegions: this.formBuilder.control<Region[]>([]),
      selectedOwners: this.formBuilder.control<Owner[]>([]),
    });
  }

  private initializeDataStreams(): void {
    // Connect to repository observables
    this.enterprises$ = combineLatest([
      this.globalPropRepo.enterprises$,
      this.enterpriseReorderTrigger$,
    ]).pipe(map(([enterprises]) => reorderItems(enterprises, this.getSelectedEnterpriseIds())));
    // Brand/Hotel options place currently-selected items at the top so the user can
    // unselect them without scrolling. Re-ordering is recomputed only when the panel
    // closes (and when the source list changes), so items don't jump while selecting.
    this.brands$ = combineLatest([this.globalPropRepo.brands$, this.brandReorderTrigger$]).pipe(
      map(([brands]) => reorderItems(brands, this.getSelectedBrandIds()))
    );
    this.hotels$ = combineLatest([this.globalPropRepo.hotels$, this.hotelReorderTrigger$]).pipe(
      map(([hotels]) => reorderItems(hotels, this.getSelectedHotelIds()))
    );

    this.owners$ = combineLatest([this.globalPropRepo.owners$, this.ownerReorderTrigger$]).pipe(
      map(([owners]) => reorderItems(owners, this.getSelectedOwnerIds()))
    );

    this.regions$ = combineLatest([this.globalPropRepo.regions$, this.regionReorderTrigger$]).pipe(
      map(([regions]) => reorderItems(regions, this.getSelectedRegionIds()))
    );

    // ✅ FETCH OWNERS & REGIONS ONCE - They are top-level, never re-fetch
    this.globalPropRepo.fetchOwners().pipe(takeUntil(this.destroy$)).subscribe();
    this.globalPropRepo.fetchRegions().pipe(takeUntil(this.destroy$)).subscribe();

    // Get existing filter state from localStorage on page reload
    // This ensures dropdown APIs receive the selected filter IDs
    this.globalFilterRepo.filter$
      .pipe(
        take(1),
        takeUntil(this.destroy$)
        // Take only the first emission (initial load from localStorage)
      )
      .subscribe((currentFilter) => {
        // Extract selected IDs (or empty arrays if nothing selected)
        const enterpriseIds = currentFilter?.enterprises || [];
        const ownerIds = currentFilter?.ownershipGroups || [];
        const brandIds = currentFilter?.brands || [];
        const regionIds = currentFilter?.regions || [];

        // Fetch dropdown data with existing filter constraints
        // Hierarchy: Enterprise → Owners → Brand → Region → Hotels

        // Always fetch all enterprises (top level)
        this.fetchEnterprises$(ownerIds, regionIds).pipe(takeUntil(this.destroy$)).subscribe();

        // Brands filtered by selected enterprises + owners
        this.fetchBrands$(enterpriseIds, ownerIds, regionIds)
          .pipe(takeUntil(this.destroy$))
          .subscribe();

        // Hotels filtered by all parent selections
        this.fetchHotels$(brandIds, enterpriseIds, ownerIds, regionIds)
          .pipe(takeUntil(this.destroy$))
          .subscribe();
      });
  }

  private syncFormFromStore(): void {
    combineLatest([
      this.globalFilterRepo.filter$,
      this.globalPropRepo.enterprises$,
      this.globalPropRepo.owners$,
      this.globalPropRepo.brands$,
      this.globalPropRepo.regions$,
      this.globalPropRepo.hotels$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([filter, enterprises, owners, brands, regions, hotels]) => {
        if (!filter) return;

        // Match IDs from filter with full entity objects
        const selectedEnterprises = enterprises.filter((e) => filter.enterprises.includes(e.id));
        const selectedOwners = owners.filter((o) => filter.ownershipGroups.includes(o.id));
        const selectedBrands = brands.filter((b) => filter.brands.includes(b.id));
        const selectedRegions = regions.filter((r) => filter.regions.includes(r.id));
        const selectedHotels = hotels.filter((h) => filter.hotels.includes(h.id));

        // Update form without triggering onChange events (avoid infinite loop)
        this.filterForm.patchValue(
          {
            selectedEnterprises,
            selectedOwners,
            selectedBrands,
            selectedRegions,
            selectedHotels,
          },
          { emitEvent: false }
        );
      });
  }

  // Enterprise search
  private setupSearchFilters(): void {
    this.enterpriseSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((search) => {
          const ownerIds = this.filterForm.value.selectedOwners?.map((o) => o.id) || [];
          const regionIds = this.filterForm.value.selectedRegions?.map((r) => r.id) || [];
          return this.fetchEnterprises$(ownerIds, regionIds, search);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    // Brand search
    this.brandSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((search) => {
          const enterpriseIds = this.filterForm.value.selectedEnterprises?.map((e) => e.id) || [];
          const ownerIds = this.filterForm.value.selectedOwners?.map((o) => o.id) || [];
          const regionIds = this.filterForm.value.selectedRegions?.map((r) => r.id) || [];
          return this.fetchBrands$(enterpriseIds, ownerIds, regionIds, search);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    // Hotel search
    this.hotelSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((search) => {
          const brandIds = this.filterForm.value.selectedBrands?.map((b) => b.id) || [];
          const enterpriseIds = this.filterForm.value.selectedEnterprises?.map((e) => e.id) || [];
          const ownerIds = this.filterForm.value.selectedOwners?.map((o) => o.id) || [];
          const regionIds = this.filterForm.value.selectedRegions?.map((r) => r.id) || [];
          return this.fetchHotels$(brandIds, enterpriseIds, ownerIds, regionIds, search);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    // Region search
    this.regionSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((search) => {
          if (search && search.trim()) {
            return this.globalPropRepo.fetchRegions(search);
          }
          return of([]);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    // Owner search
    this.ownerSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((search) => {
          if (search && search.trim()) {
            return this.globalPropRepo.fetchOwners(search);
          }
          return of([]);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  // Search handlers
  onEnterpriseSearch(event: any): void {
    const searchTerm = event.filter || '';
    this.enterpriseSearch$.next(searchTerm);
  }

  onBrandSearch(event: any): void {
    const searchTerm = event.filter || '';
    this.brandSearch$.next(searchTerm);
  }

  onHotelSearch(event: any): void {
    const searchTerm = event.filter || '';
    this.hotelSearch$.next(searchTerm);
  }

  onRegionSearch(event: any): void {
    const searchTerm = event.filter || '';
    this.regionSearch$.next(searchTerm);
  }

  onOwnerSearch(event: any): void {
    const searchTerm = event.filter || '';
    this.ownerSearch$.next(searchTerm);
  }

  // Re-order handlers: move selected items to the top when the panel closes,
  // so they are ready at the top the next time the dropdown is opened.
  onEnterprisePanelHide(): void {
    this.enterpriseReorderTrigger$.next();
  }
  onBrandPanelHide(): void {
    this.brandReorderTrigger$.next();
  }

  onHotelPanelHide(): void {
    this.hotelReorderTrigger$.next();
  }

  onOwnerPanelHide(): void {
    this.ownerReorderTrigger$.next();
  }

  onRegionPanelHide(): void {
    this.regionReorderTrigger$.next();
  }

  private getSelectedEnterpriseIds(): string[] {
    return this.filterForm.value.selectedEnterprises?.map((e) => e.id) ?? [];
  }

  private getSelectedBrandIds(): string[] {
    return this.filterForm.value.selectedBrands?.map((b) => b.id) ?? [];
  }

  private getSelectedHotelIds(): string[] {
    return this.filterForm.value.selectedHotels?.map((h) => h.id) ?? [];
  }

  private getSelectedOwnerIds(): string[] {
    return this.filterForm.value.selectedOwners?.map((o) => o.id) ?? [];
  }

  private getSelectedRegionIds(): string[] {
    return this.filterForm.value.selectedRegions?.map((r) => r.id) ?? [];
  }

  onEnterpriseChange(event: any): void {
    const enterpriseIds = event.value?.map((e: EntityRef) => e.id) || [];
    const ownerIds = this.filterForm.value.selectedOwners?.map((o) => o.id) || [];
    const regionIds = this.filterForm.value.selectedRegions?.map((r) => r.id) || [];

    // Reset ALL children when enterprise changes
    this.filterForm.patchValue(
      {
        selectedBrands: [],
        selectedHotels: [],
      },
      { emitEvent: false }
    );

    // Re-fetch all children filtered by enterprise (empty array = all)
    this.fetchBrands$(enterpriseIds, ownerIds, regionIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
    this.fetchHotels$([], enterpriseIds, ownerIds, regionIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe();

    // Save to Elf store
    this.saveToGlobalFilter();
  }

  onOwnerChange(event: any): void {
    const ownerIds = event.value?.map((o: Owner) => o.id) || [];
    const regionIds = this.filterForm.value.selectedRegions?.map((r) => r.id) || [];

    // Reset children (not parent enterprise)
    this.filterForm.patchValue(
      {
        selectedEnterprises: [],
        selectedBrands: [],
        // selectedRegions: [],
        selectedHotels: [],
      },
      { emitEvent: false }
    );

    // Re-fetch children filtered by owner + region (enterprise was reset above)
    this.fetchEnterprises$(ownerIds, regionIds).pipe(takeUntil(this.destroy$)).subscribe();
    this.fetchBrands$([], ownerIds, regionIds).pipe(takeUntil(this.destroy$)).subscribe();
    this.fetchHotels$([], [], ownerIds, regionIds).pipe(takeUntil(this.destroy$)).subscribe();

    // Save to Elf store
    this.saveToGlobalFilter();
  }

  onBrandChange(event: any): void {
    const enterpriseIds = this.filterForm.value.selectedEnterprises?.map((e) => e.id) || [];
    const ownerIds = this.filterForm.value.selectedOwners?.map((o) => o.id) || [];
    const brandIds = event.value?.map((b: any) => b.id) || [];
    const regionIds = this.filterForm.value.selectedRegions?.map((r) => r.id) || [];

    // Reset children (not parents)
    this.filterForm.patchValue(
      {
        selectedHotels: [],
      },
      { emitEvent: false }
    );

    this.fetchHotels$(brandIds, enterpriseIds, ownerIds, regionIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe();

    // Save to Elf store
    this.saveToGlobalFilter();
  }

  onRegionChange(event: any): void {
    const ownerIds = this.filterForm.value.selectedOwners?.map((o) => o.id) || [];
    const regionIds = event.value?.map((r: any) => r.id) || [];

    this.filterForm.patchValue(
      {
        selectedEnterprises: [],

        selectedBrands: [],

        selectedHotels: [],
      },
      { emitEvent: false }
    );

    // Re-fetch children filtered by owner + region (enterprise/brand were reset above)
    this.fetchEnterprises$(ownerIds, regionIds).pipe(takeUntil(this.destroy$)).subscribe();
    this.fetchBrands$([], ownerIds, regionIds).pipe(takeUntil(this.destroy$)).subscribe();
    this.fetchHotels$([], [], ownerIds, regionIds).pipe(takeUntil(this.destroy$)).subscribe();

    // Save to Elf store
    this.saveToGlobalFilter();
  }

  onHotelChange(event: any): void {
    // Handle single hotel selection mode (e.g., Property Report page)
    if (this.selectSingleHotel) {
      const selectedHotels: GlobalHotel[] = event.value || [];

      if (selectedHotels.length > 0) {
        // In single-select mode, keep only the LAST selected hotel (most recent click)
        const singleHotel = selectedHotels[selectedHotels.length - 1];

        // Replace form value with only this one hotel
        this.filterForm.patchValue(
          {
            selectedHotels: [singleHotel],
          },
          { emitEvent: false }
        );

        // Store hotel info for display in UI (e.g., Property Report title)
        this.globalFilterRepo.setSelectedHotelInfo({
          id: singleHotel.id,
          name: singleHotel.name,
          code: singleHotel.code || '',
          displayName: singleHotel.displayName,
        });
      } else {
        // Hotel deselected - clear hotel info
        this.globalFilterRepo.setSelectedHotelInfo(null);
      }

      this.saveToGlobalFilter();
      return;
    }

    // Multi-select mode (default behavior)
    // Hotels are the lowest level - no children to reset
    this.globalFilterRepo.setSelectedHotelInfo(null);
    this.saveToGlobalFilter();
  }

  // Chip removal methods - maintain hierarchical filter logic
  removeEnterpriseChip(enterpriseId: string): void {
    const currentEnterprises = this.filterForm.value.selectedEnterprises || [];
    const updatedEnterprises = currentEnterprises.filter((e) => e.id !== enterpriseId);
    const enterpriseIds = updatedEnterprises.map((e) => e.id);
    const currentOwnersIds = this.filterForm.value.selectedOwners?.map((o) => o.id) || [];
    const currentRegionsIds = this.filterForm.value.selectedRegions?.map((r) => r.id) || [];

    // Reset ALL children when enterprise is removed
    this.filterForm.patchValue(
      {
        selectedEnterprises: updatedEnterprises,
        // selectedOwners: [],
        selectedBrands: [],
        // selectedRegions: [],
        selectedHotels: [],
      },
      { emitEvent: false }
    );

    // Re-fetch all children filtered by remaining enterprises
    this.fetchBrands$(enterpriseIds, currentOwnersIds, currentRegionsIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
    this.fetchHotels$([], enterpriseIds, currentOwnersIds, currentRegionsIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe();

    // Save to Elf store
    this.saveToGlobalFilter();
  }

  removeOwnerChip(ownerId: string): void {
    const currentOwners = this.filterForm.value.selectedOwners || [];
    const updatedOwners = currentOwners.filter((o) => o.id !== ownerId);
    const ownerIds = updatedOwners.map((o) => o.id);
    const regionIds = this.filterForm.value.selectedRegions?.map((r) => r.id) || [];

    // Reset children (not parent enterprise)
    this.filterForm.patchValue(
      {
        selectedOwners: updatedOwners,
        selectedEnterprises: [],
        selectedBrands: [],
        selectedHotels: [],
      },
      { emitEvent: false }
    );

    // Re-fetch children filtered by enterprise + remaining owners
    this.fetchEnterprises$(ownerIds, regionIds).pipe(takeUntil(this.destroy$)).subscribe();
    this.fetchBrands$([], ownerIds, regionIds).pipe(takeUntil(this.destroy$)).subscribe();
    this.fetchHotels$([], [], ownerIds, regionIds).pipe(takeUntil(this.destroy$)).subscribe();

    // Save to Elf store
    this.saveToGlobalFilter();
  }

  removeRegionChip(regionId: string): void {
    const currentRegions = this.filterForm.value.selectedRegions || [];
    const updatedRegions = currentRegions.filter((r) => r.id !== regionId);
    const ownerIds = this.filterForm.value.selectedOwners?.map((o) => o.id) || [];
    const regionIds = updatedRegions.map((r) => r.id);

    // Reset only hotels (not other parents)
    this.filterForm.patchValue(
      {
        selectedRegions: updatedRegions,
        selectedEnterprises: [],
        selectedBrands: [],
        selectedHotels: [],
      },
      { emitEvent: false }
    );

    // Re-fetch hotels filtered by all parents + remaining regions
    this.globalPropRepo
      .fetchEnterprises(ownerIds, regionIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
    this.globalPropRepo
      .fetchBrands([], ownerIds, regionIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
    this.globalPropRepo
      .fetchHotels([], [], ownerIds, regionIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe();

    // Save to Elf store
    this.saveToGlobalFilter();
  }

  // Single method to save to GlobalFilterRepository (single source of truth)
  private saveToGlobalFilter(): void {
    const value = this.filterForm.value;

    const filter: GlobalFilter = {
      enterprises: value.selectedEnterprises?.map((e) => e.id) || [],
      ownershipGroups: value.selectedOwners?.map((o) => o.id) || [],
      brands: value.selectedBrands?.map((b) => b.id) || [],
      regions: value.selectedRegions?.map((r) => r.id) || [],
      hotels: value.selectedHotels?.map((h) => h.id) || [],
    };

    this.globalFilterRepo.setFilter(filter);
  }

  toggleAdvancedFilterDrawer(): void {
    this.drawerVisible = !this.drawerVisible;
  }

  showDrawer(): void {
    this.drawerVisible = true;
  }

  hideDrawer(): void {
    this.drawerVisible = false;
  }

  onDateChange(dateRange: DateRange): void {
    // Emit the date change event so parent components can react
    this.dateRangeChanged.emit(dateRange);
  }

  onFilterButtonClick() {
    this.drawerVisible = false;
  }

  resetAllFilters(): void {
    this.filterForm.reset({
      selectedBrands: [],
      selectedRegions: [],
      selectedHotels: [],
      selectedEnterprises: [],
      selectedOwners: [],
    });

    // Clear Elf store (auto-persists to localStorage)
    this.globalFilterRepo.setFilter({
      enterprises: [],
      ownershipGroups: [],
      brands: [],
      regions: [],
      hotels: [],
    });
  }

  clearAndCloseFilters(): void {
    this.resetAllFilters();
    this.drawerVisible = false;
  }

  // HOST LISTENERS (scroll detection)
  @HostListener('window:scroll')
  onWindowScroll() {
    this.hideAllDropdowns();
  }

  // PRIVATE METHODS (hide all dropdowns on scroll)
  private hideAllDropdowns() {
    if (this.multiSelects) {
      this.multiSelects.forEach((multiSelect) => {
        if (multiSelect.overlayVisible) {
          multiSelect.hide();
        }
      });
    }
  }
}
